import React from "react"
import * as api from "api/guests"
import { updateSelectedGuestsStatuses } from "context/actions"
import pluralize from "pluralize"
import Icon from "components/elements/Icon"
import { Guest, GuestStatus } from "sharedTypes"
import { successToast } from "utilities/toasts"
import { Box, Flex, Text } from "@chakra-ui/react"
import * as Yup from "yup"
import { useQueryClient } from "react-query"
import { useParams } from "react-router-dom"
import useGuestsStore from "services/useGuestsStore"
import { useShallow } from "zustand/shallow"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import RadioButtons from "components/ReactHookForm/RadioButtons"
import Checkbox from "components/ReactHookForm/Checkbox"
import UpgradeAlert from "components/elements/UpgradeAlert"
import useCurrentPlanType from "utilities/useCurrentPlanType"
import { useCurrentEvent } from "queries/events"
import useFeatureFlag from "services/featureFlags"
import Popover from "components/dialogs/Popover"

type Props = {
  onClose: () => void;
  guest?: Guest;
  updateGuest?: (params: {}) => Promise<void>;
  selectedCount?: number;
  selectedStatusValue?: number;
}

const ChangeStatus = ({
  onClose, guest, updateGuest, selectedCount, selectedStatusValue,
}: Props) => {
  const queryClient = useQueryClient()
  const { eventId } = useParams<{ eventId: string }>()
  const { corporateFeaturesEnabled } = useCurrentPlanType()
  const { data: { capacity, counts } } = useCurrentEvent()
  const { eventCapacity: eventCapacityEnabled } = useFeatureFlag()
  const {
    selectedGuestIds,
    deselectAll,
    allSelected,
    groupId,
    segmentId,
  } = useGuestsStore(
    useShallow((state) => ({
      selectedGuestIds: state.selectedGuestIds,
      deselectAll: state.deselectAll,
      allSelected: state.allSelected,
      groupId: state.groupId,
      segmentId: state.segmentId,
    })),
  )

  const statuses = [
    { label: "Confirmed", value: GuestStatus.Confirmed.toString(), color: "blue.400" },
    { label: "Unconfirmed", value: GuestStatus.Unconfirmed.toString(), color: "gray.400" },
    { label: "Waitlisted", value: GuestStatus.Waitlisted.toString(), color: "yellow.300" },
    { label: "Declined", value: GuestStatus.Declined.toString(), color: "red.400" },
  ].filter(Boolean) as {label: string; value: string; color: string}[]
  const selectedStatus = statuses.find(({ value }) => value === selectedStatusValue?.toString())

  let onSubmit
  let subject

  if (guest && updateGuest) {
    subject = `${guest.personInfo.firstName} ${guest.personInfo.lastName}`
    onSubmit = ({ status, sendConfirmationEmail }) => {
      updateGuest({
        status,
        sendConfirmationEmail: sendConfirmationEmail || undefined,
      }).then(() => {
        onClose()
      })
    }
  } else {
    subject = `${selectedCount} ${pluralize("guest", selectedCount)}`
    onSubmit = ({ status, sendConfirmationEmail }) => api.patchGuestsStatuses(
      eventId,
      status,
      selectedGuestIds,
      allSelected ? segmentId : undefined,
      allSelected ? groupId : undefined,
      sendConfirmationEmail,
    ).then(() => {
      updateSelectedGuestsStatuses(
        queryClient,
        eventId,
        selectedGuestIds,
        allSelected,
        deselectAll,
        status,
      )

      const statusLabel = statuses.find(({ value }) => value === status)?.label
      successToast({ title: `Successfully set the status of ${selectedCount} ${pluralize("guest", selectedCount)} to ${statusLabel}` })
      onClose()
    }).catch(() => {
      // API service automatically alerts the user, show must go on without closing the modal
    })
  }

  return (
    <ModalForm
      initialValues={{
        status: selectedStatusValue?.toString(),
        sendConfirmationEmail: false,
      }}
      validationSchema={Yup.object().shape({ status: Yup.string().required() })}
      onSubmit={onSubmit}
      onClose={onClose}
      title="Change Guest Status"
      submitText="Apply"
    >
      {({ control, watch }) => (
        <>
          <Text fontSize="xl" mb={6}>Update the status of <Text as="strong" fontWeight="semibold">{subject}</Text> to:</Text>
          {selectedStatus ? (
            <Box
              borderRadius="full"
              borderWidth={1}
              borderColor={selectedStatus.color}
              fontSize="sm"
              backgroundColor="gray.100"
              display="inline-block"
              pr={4}
              mb={6}
            >
              <Label color={selectedStatus.color} text={selectedStatus.label} />
            </Box>
          )
            : (
              <RadioButtons
                name="status"
                control={control}
                options={statuses.map(({ label, value, color }) => ({
                  label: <Label color={color} text={label} />,
                  value,
                }))}
              />
            )}
          {eventCapacityEnabled && capacity && watch("status") === GuestStatus.Confirmed.toString() && (
            <CapacityWarning
              capacity={capacity}
              confirmedGuests={counts.confirmedGuests}
              guest={guest}
              selectedCount={selectedCount}
            />
          )}
          <Text fontSize="xl" mb={6}>Notify guests:</Text>
          <Checkbox label="Send guest confirmation email about their updated status" name="sendConfirmationEmail" control={control} disabled={!corporateFeaturesEnabled} />
          {!corporateFeaturesEnabled && (
            <Box textColor="blue.400" ml={7} mt={-4}>
              <UpgradeAlert text="This feature requires a Corporate Subscription." noBorder />
            </Box>
          )}
        </>
      )}
    </ModalForm>
  )
}

type CapacityWarningProps = {
  capacity: number
  confirmedGuests: number
  guest?: Guest
  selectedCount?: number
}

// Never blocks: the organizer can go over capacity
const CapacityWarning = ({
  capacity, confirmedGuests, guest, selectedCount = 0,
}: CapacityWarningProps) => {
  const spotsLeft = capacity - confirmedGuests
  let warning

  if (guest) {
    if (guest.status !== GuestStatus.Confirmed && spotsLeft < 1) {
      warning = `This puts the event over capacity (${confirmedGuests + 1} / ${capacity}).`
    }
  } else if (spotsLeft < 1) {
    warning = `The event is at capacity (${confirmedGuests} / ${capacity}). Confirming more guests puts it over.`
  } else if (selectedCount > spotsLeft) {
    warning = `Only ${spotsLeft} ${pluralize("spot", spotsLeft)} left (${confirmedGuests} / ${capacity}). Confirming ${selectedCount} guests may put the event over capacity.`
  }

  if (!warning) return null

  return (
    <Flex align="center" gap={2} mt={-2} mb={6} color="orange.600">
      <Text>{warning}</Text>
      <Popover
        triggerEvent="hover"
        placement="top"
        trigger={<Flex><Icon icon="info" color="gray.500" /></Flex>}
        body={(
          <Text p={4} color="gray.700">
            The event capacity is set on the Forms page, where you can change it.
            Guest forms close when it&apos;s reached; your team can still confirm guests.
          </Text>
        )}
      />
    </Flex>
  )
}

const Label = ({ color, text }) => (
  <Flex align="center" fontSize="lg">
    <Icon icon="dot" ml={2} mr={3} color={color} /> {text}
  </Flex>
)

export default ChangeStatus
